import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbDatepickerI18n, NgbDateStruct, NgbCalendar, NgbDate, NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { SbDateISOParserFormatter } from '../../../services/date-time/date-formatter-iso.service';
import { SbDateStructAdapter } from '../../../services/date-time/ngb-sb-date-struct-adapter.service';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SbLocalDateInputComponent } from '../sb-local-date-input/sb-local-date-input.component';
import { SbDateTime, SbLocalDate } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { UtilService } from '../../../services/util.service';

import { NgbTranslatedDatepicker } from '../../../services/date-time/ngb-datepicker-translated.service';

@Component({
  selector: 'sb-local-date-range-input',
  templateUrl: './sb-local-date-range-input.component.html',
  styleUrls: ['./sb-local-date-range-input.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: NgbTranslatedDatepicker },
    { provide: NgbDateParserFormatter, useClass: SbDateISOParserFormatter },
    { provide: NgbDateAdapter, useClass: SbDateStructAdapter },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SbLocalDateInputComponent), multi: true },
  ],
  standalone: false,
})
export class SbLocalDateRangeInputComponent implements OnInit {
  @ViewChild('datepicker') datepicker: ElementRef;

  hoveredDate: NgbDate | null = null;
  @Input() weekNumbers: boolean = true;
  @Input() showSubmit: boolean = true;
  @Input() disabled: boolean = false;
  @Input() allowEmpty: boolean = false;
  @Input() displayMonths: 1 | 2 = 2;

  enableApply: boolean;
  visible: boolean;

  // tslint:disable-next-line:variable-name
  _dateStringFrom: string;
  set dateStringFrom(value: string) {
    this._dateStringFrom = value;
  }

  get dateStringFrom(): string {
    return this._dateStringFrom;
  }

  // tslint:disable-next-line:variable-name
  _dateStringTo: string;
  set dateStringTo(value: string) {
    this._dateStringTo = value;
  }

  get dateStringTo(): string {
    return this._dateStringTo;
  }

  // tslint:disable-next-line:variable-name
  _from: SbDateTime;
  @Input() set from(value: SbDateTime) {
    this._from = value;
    if (value) {
      this.dateStringFrom = this.util.dateToDateString(value.value);
      this.setDate(value, 'from');
    }
  }

  // tslint:disable-next-line:variable-name
  _till: SbDateTime;
  @Input() set till(value: SbDateTime) {
    this._till = value;
    if (value) {
      this.dateStringTo = this.util.dateToDateString(value.value);
      this.setDate(value, 'to');
    }
  }

  // tslint:disable-next-line:variable-name
  _dateStructFrom: SbDateTime;
  set dateStructFrom(value: SbDateTime) {
    this._dateStructFrom = value;
  }

  get dateStructFrom(): SbDateTime {
    return this._dateStructFrom;
  }

  // tslint:disable-next-line:variable-name
  _dateStructTo: SbDateTime;
  set dateStructTo(value: SbDateTime) {
    this._dateStructTo = value;
  }

  get dateStructTo(): SbDateTime {
    return this._dateStructTo;
  }

  // tslint:disable-next-line:variable-name
  private _fullDateString: string = '';
  get fullDateString(): string {
    return this._fullDateString;
  }

  set fullDateString(value: string) {
    this._fullDateString = value;
  }

  // tslint:disable-next-line:variable-name
  _today: NgbDate;
  @Input('today')
  set today(date: NgbDate) {
    if (date === null) {
      this._today = undefined;
    } else {
      if (date === undefined) {
        this._today = this.calendar.getToday();
      } else {
        this._today = date;
      }
    }
  }

  get today(): NgbDate {
    return this._today;
  }

  fromDate: NgbDateStruct;
  toDate: NgbDateStruct | null = null;

  @Output()
  dateChange: EventEmitter<{ fromDate: SbDateTime; toDate: SbDateTime }> = new EventEmitter<{ fromDate: SbDateTime; toDate: SbDateTime }>();

  constructor(
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    public util: UtilService
  ) {
    this.today = this.calendar.getToday();
  }

  ngOnInit(): void {}

  setDate(sbDate: SbDateTime, type: string) {
    if (type === 'from') {
      this.fromDate = {
        year: sbDate.value.year,
        month: sbDate.value.month,
        day: sbDate.value.day,
      };
    }
    if (type === 'to') {
      this.toDate = {
        year: sbDate.value.year,
        month: sbDate.value.month,
        day: sbDate.value.day,
      };
    }
    this._fullDateString = (this.dateStringFrom ? this.dateStringFrom : '') + ' - ' + (this.dateStringTo ? this.dateStringTo : '');
  }

  onDateSelection(date: NgbDate) {
    this.enableApply = false;
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && (date.equals(this.fromDate) || date.after(this.fromDate))) {
      this.toDate = date;
      this.enableApply = true;
      if (!this.showSubmit) {
        this.onDateApply();
      }
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  onDateApply() {
    this.convertDate(this.fromDate, 'from');
    this.convertDate(this.toDate, 'to');
    this._fullDateString = !this.dateStringFrom && !this.dateStringTo ? null : this.dateStringFrom + ' - ' + this.dateStringTo;
    this.dateChange.emit({ fromDate: this.dateStructFrom, toDate: this.dateStructTo });
    this.visible = false;
  }

  convertDate(date: NgbDateStruct, type: string) {
    const dt: SbDateTime = new SbDateTime();
    dt.value = DateTime.local();
    if (type === 'from') {
      if (date) {
        dt.value = dt.value.set({
          day: date.day,
          month: date.month,
          year: date.year,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
        this.dateStructFrom = dt;
        this.dateStringFrom = this.util.dateToDateString(dt.value);
      } else {
        this.dateStructFrom = null;
        this.dateStringFrom = '';
      }
    } else if (type === 'to') {
      if (date) {
        dt.value = dt.value.set({
          day: date.day,
          month: date.month,
          year: date.year,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
        this.dateStructTo = dt;
        this.dateStringTo = this.util.dateToDateString(dt.value);
      } else {
        this.dateStructTo = null;
        this.dateStringTo = '';
      }
    }
  }

  clearDate() {
    this.fromDate = null;
    this.toDate = null;
    this._fullDateString = '';
    this.enableApply = false;
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
  }

  isFirstDayRange(date: NgbDate) {
    return date.equals(this.fromDate);
  }

  isLastDayRange(date: NgbDate) {
    return date.equals(this.toDate);
  }

  /*  validateInput(currentValue: NgbDateStruct | null, input: string): NgbDate | null {
      const parsed = this.formatter.parse(input);
      return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
    }*/

  formatDate(from, till) {
    return this.formatter.format(from) + '-' + this.formatter.format(till);
  }

  toggleDatepicker() {
    this.visible = !this.visible;
  }

  refreshFromDateString() {
    if (this.fullDateString && this.fullDateString.length > 0) {
      const dateStrings = this._fullDateString.split('-').map(date => date.trim());
      const dates: SbLocalDate[] = [];
      for (const dateStr of dateStrings) {
        const date = this.util.parseDateFromDateString(dateStr);
        if (date) {
          dates.push(new SbLocalDate(date));
        }
      }
      if (dates.length === 2) {
        this.fromDate = dates[0].value;
        this.toDate = dates[1].value;
      }
    }
    if (this.fromDate <= this.toDate) {
      this.onDateApply();
    }
  }
}
